import logger from '~/services/logger';
import { getURLSegments } from './validations';
import { convertAndParseStringArray } from '~/services/layout/layout';
import type { Feed, UIComponent } from '~/services/layout/index.ts';

export const LANGUAGE_SELECTOR_DEFAULT_LANGUAGE = 'en';
export const LANGUAGE_SELECTOR_CLIENT_STORAGE_KEY = 'language';
export const LANGUAGE_SELECTOR_ENDPOINT = '/api/language-selector';

/**
 * getSupportedLanguages returns languages supported by
 * the application defined in the envrionment variable `languages`.
 * In case of exception - function returns English as a default language.
 */
export function getSupportedLanguages(): string[] {
  const languagesString: string | undefined = process?.env?.languages;

  if (!languagesString) return [LANGUAGE_SELECTOR_DEFAULT_LANGUAGE];

  const parseLanguagesString: string[] =
    convertAndParseStringArray(languagesString);

  try {
    if (parseLanguagesString.length === 0)
      throw new Error('Empty production languages array');

    return parseLanguagesString;
  } catch (error: any) {
    logger.info(
      `Failed to parse languages env var, returning [${LANGUAGE_SELECTOR_DEFAULT_LANGUAGE}]\nerror: ${error}`
    );

    return [LANGUAGE_SELECTOR_DEFAULT_LANGUAGE];
  }
}

/**
 * getURLLanguage returns first segment of the URL assuming
 * it's a language code. If the retreived language code is not
 * supported - function returns 'unknown' string.
 */
export function getURLLanguage(pathname: string): string | 'unknown' {
  const segments = getURLSegments(pathname);
  const supportedLanguages = getSupportedLanguages();

  const urlLanguage: string | undefined =
    supportedLanguages.find((lang) => lang === segments[0]) || 'unknown';

  return urlLanguage;
}

/**
 * Get the language labels for the supported languages based on the default theme v2 localizations.
 * @param supportedLanguages - array of supported languages
 * @param pluginConfigurations - array of plugin configurations to get the default theme config and its localizations
 * @returns an object with language codes as keys and their corresponding labels as values
 */
export function getLanguagesLabels({
  supportedLanguages,
  pluginConfigurations,
}: {
  supportedLanguages: string[];
  pluginConfigurations: any;
}) {
  try {
    const defaultThemeV2Config = pluginConfigurations.find(
      (config: any) => config.plugin?.identifier === 'default_theme_v2'
    );

    return supportedLanguages.reduce((acc: Record<string, string>, lang) => {
      acc[lang] =
        defaultThemeV2Config?.configuration_json?.localizations?.[lang]
          ?.language_label || lang;
      return acc;
    }, {});
  } catch (error: any) {
    logger.error(`Error generating language labels: ${error.message}`);
    return {};
  }
}

/**
 * Generate language selector feed.
 * @param languagesLabels - A record of language codes and their corresponding labels.
 * @param uiComponent - The UI component for which the feed is being generated.
 * @returns A feed object containing entries for each supported language.
 */
export function generateLanguageSelectorFeed({
  languagesLabels,
  uiComponent,
}: {
  languagesLabels: Record<string, string>;
  uiComponent: UIComponent;
}): Feed {
  const uiComponentId = uiComponent?.id;

  const baseLanguageSelectorFeed = {
    extensions: {
      role: 'language_selector',
    },
    id: `language-selector-feed-${uiComponentId}`,
    title: '',
    type: {
      value: 'feed',
    },
  };

  try {
    const supportedLanguages = Object.keys(languagesLabels);

    const feedEntries = supportedLanguages.map((lang, i) => {
      const title = languagesLabels[lang] || lang;

      return {
        _role: 'language_selector',
        extensions: {
          tag: lang,
        },
        id: `language-selector-entry-${lang}-${uiComponentId}-${i}`,
        title,
        type: {
          value: 'action',
        },
      };
    });

    return {
      entry: feedEntries,
      ...baseLanguageSelectorFeed,
    };
  } catch (error: any) {
    logger.error(`Error generating language selector feed: ${error.message}`);
    return {
      entry: [],
      ...baseLanguageSelectorFeed,
    };
  }
}
